import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ProductShowcase.module.css';

/**
 * Interactive product showcase for the marketing homepage.
 *
 * Renders a tabbed "app window" with faithful mock-ups of the four core
 * Realty Genie surfaces (compliance dashboard, document tracking, listing
 * management, education hub). All data below is illustrative sample data –
 * no real agency or client information is used.
 */

type TabKey = 'dashboard' | 'documents' | 'listings' | 'education';

interface TabDef {
  key: TabKey;
  label: string;
  blurb: string;
}

const TABS: TabDef[] = [
  {
    key: 'dashboard',
    label: 'Compliance Dashboard',
    blurb: 'One score that tells a Principal exactly how audit-ready the agency is right now.',
  },
  {
    key: 'documents',
    label: 'Document Tracking',
    blurb: 'Every FICA, PPRA and POPIA document per agent – with expiry dates watched for you.',
  },
  {
    key: 'listings',
    label: 'Listing Management',
    blurb: 'Mandates, offers and listing paperwork tracked from instruction to transfer.',
  },
  {
    key: 'education',
    label: 'Education Hub',
    blurb: 'Guided workflows and short courses that keep agents current on the regulations.',
  },
];

const AUTO_ADVANCE_MS = 7000;

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const Donut: React.FC<{ value: number; caption: string; sub: string }> = ({ value, caption, sub }) => (
  <div className={styles.donut}>
    <div
      className={styles.donutRing}
      style={{
        background: `conic-gradient(#26a69a ${value}%, rgba(255,255,255,0.12) ${value}% 100%)`,
      }}
    >
      <div className={styles.donutHole}>
        <span className={styles.donutValue}>{value}%</span>
      </div>
    </div>
    <div className={styles.donutMeta}>
      <strong>{caption}</strong>
      <span>{sub}</span>
    </div>
  </div>
);

const Bar: React.FC<{ label: string; done: number; total: number }> = ({ label, done, total }) => {
  const pct = total === 0 ? 100 : Math.round((done / total) * 100);
  const complete = done === total;
  return (
    <div className={styles.barRow}>
      <span className={styles.barLabel}>{label}</span>
      <span className={styles.barTrack}>
        <span
          className={complete ? `${styles.barFill} ${styles.barFillDone}` : styles.barFill}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className={styles.barCount}>
        {done}/{total}
      </span>
    </div>
  );
};

type Status = 'valid' | 'expiring' | 'expired' | 'missing';

const STATUS_LABEL: Record<Status, string> = {
  valid: 'Valid',
  expiring: 'Expiring',
  expired: 'Expired',
  missing: 'Missing',
};

const Chip: React.FC<{ status: Status }> = ({ status }) => (
  <span className={`${styles.chip} ${styles[`chip_${status}`]}`}>{STATUS_LABEL[status]}</span>
);

/* ------------------------------------------------------------------ */
/* Panels                                                             */
/* ------------------------------------------------------------------ */

const DashboardPanel: React.FC = () => (
  <div className={styles.panelGrid}>
    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.cardTitle}>Compliance score</div>
      <Donut value={87} caption="128 / 147 items complete" sub="Agency-wide, all agents" />
    </div>

    <div className={styles.card}>
      <div className={styles.cardTitle}>Document health</div>
      <div className={styles.miniStats}>
        <div><strong className={styles.ok}>112</strong><span>Valid</span></div>
        <div><strong className={styles.warn}>9</strong><span>Expiring</span></div>
        <div><strong className={styles.bad}>4</strong><span>Expired</span></div>
        <div><strong className={styles.muted}>22</strong><span>Missing</span></div>
      </div>
    </div>

    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.cardTitle}>Needs attention</div>
      <ul className={styles.attentionList}>
        <li>
          <span className={styles.dotWarn} />
          <span>FICA – Proof of address expires in <strong>6 days</strong></span>
          <span className={styles.attentionWho}>T. Mokoena</span>
        </li>
        <li>
          <span className={styles.dotBad} />
          <span>PPRA Fidelity Fund Certificate <strong>expired</strong></span>
          <span className={styles.attentionWho}>L. Ndlovu</span>
        </li>
        <li>
          <span className={styles.dotMuted} />
          <span>POPIA consent form <strong>not uploaded</strong></span>
          <span className={styles.attentionWho}>2 Sandton listings</span>
        </li>
      </ul>
    </div>
  </div>
);

const DOC_CATEGORIES: { label: string; done: number; total: number }[] = [
  { label: 'FICA Verification', done: 8, total: 8 },
  { label: 'Financial', done: 6, total: 7 },
  { label: 'Consent Forms (POPIA)', done: 2, total: 2 },
  { label: 'Legal Agreements', done: 4, total: 6 },
  { label: 'Qualification & CPD', done: 12, total: 14 },
];

const DOC_ROWS: { name: string; status: Status; note: string }[] = [
  { name: 'Certified ID copy', status: 'valid', note: 'Verified 12 Feb 2026' },
  { name: 'Proof of address', status: 'expiring', note: 'Expires 06 Sep 2026' },
  { name: 'Fidelity Fund Certificate', status: 'valid', note: 'Valid to 31 Dec 2026' },
  { name: 'Tax clearance', status: 'missing', note: 'Not uploaded' },
];

const DocumentsPanel: React.FC = () => (
  <div className={styles.panelGrid}>
    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.docHeader}>
        <div className={styles.cardTitle}>Documents for</div>
        <span className={styles.agentPill}>Lerato Ndlovu · Agent</span>
        <span className={styles.completionTag}>74% complete</span>
      </div>
      <div className={styles.bars}>
        {DOC_CATEGORIES.map((c) => (
          <Bar key={c.label} label={c.label} done={c.done} total={c.total} />
        ))}
      </div>
    </div>

    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.cardTitle}>FICA Verification</div>
      <ul className={styles.docList}>
        {DOC_ROWS.map((d) => (
          <li key={d.name}>
            <span className={styles.docName}>{d.name}</span>
            <span className={styles.docNote}>{d.note}</span>
            <Chip status={d.status} />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const LISTINGS: {
  address: string;
  suburb: string;
  province: string;
  type: 'Sale' | 'Rental';
  status: 'Open' | 'Pending' | 'Closed';
  docs: string;
}[] = [
  { address: '12 Oak Avenue', suburb: 'Bryanston', province: 'Gauteng', type: 'Sale', status: 'Open', docs: '9 / 10' },
  { address: '4 Marine Drive', suburb: 'Sea Point', province: 'Western Cape', type: 'Rental', status: 'Pending', docs: '7 / 7' },
  { address: '88 Ridge Road', suburb: 'Morningside', province: 'KwaZulu-Natal', type: 'Sale', status: 'Open', docs: '6 / 10' },
  { address: '21 Protea Street', suburb: 'Centurion', province: 'Gauteng', type: 'Sale', status: 'Closed', docs: '10 / 10' },
];

const ListingsPanel: React.FC = () => (
  <div className={styles.panelGrid}>
    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.miniStats}>
        <div><strong>24</strong><span>Total</span></div>
        <div><strong className={styles.ok}>15</strong><span>Open</span></div>
        <div><strong className={styles.warn}>6</strong><span>Pending</span></div>
        <div><strong className={styles.muted}>3</strong><span>Closed</span></div>
      </div>
    </div>

    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.cardTitle}>Listings</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Address</th>
              <th>Suburb</th>
              <th>Province</th>
              <th>Type</th>
              <th>Status</th>
              <th>Docs</th>
            </tr>
          </thead>
          <tbody>
            {LISTINGS.map((l) => (
              <tr key={l.address}>
                <td>{l.address}</td>
                <td>{l.suburb}</td>
                <td>{l.province}</td>
                <td>{l.type}</td>
                <td>
                  <span className={`${styles.statusPill} ${styles[`status_${l.status}`]}`}>{l.status}</span>
                </td>
                <td>{l.docs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const GUIDES: { title: string; tag: string; done: boolean }[] = [
  { title: 'Preparing a listing for FICA compliance', tag: 'Compliance', done: true },
  { title: 'POPIA consent essentials for agents', tag: 'POPIA', done: true },
  { title: 'PPRA mandate checklist, step by step', tag: 'PPRA', done: false },
  { title: 'Verifying a beneficial owner', tag: 'FICA', done: false },
];

const EducationPanel: React.FC = () => (
  <div className={styles.panelGrid}>
    <div className={styles.card}>
      <div className={styles.cardTitle}>Your progress</div>
      <Donut value={44} caption="4 / 9 guides completed" sub="Keep your CPD current" />
    </div>

    <div className={styles.card}>
      <div className={styles.cardTitle}>What&apos;s new</div>
      <ul className={styles.newsList}>
        <li><span className={styles.newsTag}>Product</span> Digital signatures now supported</li>
        <li><span className={styles.newsTag}>Legislation</span> Updated FICA RMCP guidance</li>
        <li><span className={styles.newsTag}>Release</span> v0.9 – bulk document upload</li>
      </ul>
    </div>

    <div className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.cardTitle}>Recent guides</div>
      <ul className={styles.guideList}>
        {GUIDES.map((g) => (
          <li key={g.title}>
            <span className={g.done ? `${styles.guideCheck} ${styles.guideCheckDone}` : styles.guideCheck}>
              {g.done ? '✓' : ''}
            </span>
            <span className={styles.guideTitle}>{g.title}</span>
            <span className={styles.guideTag}>{g.tag}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PANELS: Record<TabKey, React.FC> = {
  dashboard: DashboardPanel,
  documents: DocumentsPanel,
  listings: ListingsPanel,
  education: EducationPanel,
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

const ProductShowcase: React.FC = () => {
  const [active, setActive] = useState<TabKey>('dashboard');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (paused) {
      stop();
      return;
    }
    timerRef.current = window.setInterval(() => {
      setActive((cur) => {
        const idx = TABS.findIndex((t) => t.key === cur);
        return TABS[(idx + 1) % TABS.length].key;
      });
    }, AUTO_ADVANCE_MS);
    return stop;
  }, [paused, stop]);

  const select = (key: TabKey) => {
    setPaused(true);
    setActive(key);
  };

  const activeTab = TABS.find((t) => t.key === active) ?? TABS[0];
  const Panel = PANELS[active];

  return (
    <section
      className={styles.showcase}
      id="showcase"
      onMouseEnter={() => setPaused(true)}
      onFocusCapture={() => setPaused(true)}
    >
      <div className={styles.header}>
        <h2>See exactly what your agency gets</h2>
        <p>
          Realty Genie turns scattered spreadsheets, email folders and paper files into one live
          compliance picture. Here is what the day-to-day looks like.
        </p>
      </div>

      <div className={styles.tabBar} role="tablist" aria-label="Product areas">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            id={`showcase-tab-${t.key}`}
            aria-selected={t.key === active}
            aria-controls={`showcase-panel-${t.key}`}
            className={t.key === active ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => select(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <p className={styles.blurb}>{activeTab.blurb}</p>

      <div className={styles.window}>
        <div className={styles.windowBar}>
          <span className={styles.windowDot} />
          <span className={styles.windowDot} />
          <span className={styles.windowDot} />
          <span className={styles.windowUrl}>app.realty-genie.co.za/{active}</span>
        </div>
        <div
          className={styles.windowBody}
          role="tabpanel"
          id={`showcase-panel-${active}`}
          aria-labelledby={`showcase-tab-${active}`}
          key={active}
        >
          <Panel />
        </div>
      </div>

      <p className={styles.disclaimer}>Sample data shown for illustration.</p>
    </section>
  );
};

export default ProductShowcase;
